import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '../../common';

/**
 * AcceptInvitePage - Page to accept workspace invitations
 *
 * User flow:
 * 1. User receives email with invite link containing token
 * 2. User clicks link → Redirected to this page
 * 3. Page validates token and accepts invite
 * 4. User is redirected to login or dashboard
 */
const AcceptInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false); // Prevent double execution

  useEffect(() => {
    // Prevent double execution (React.StrictMode in dev mode mounts components twice)
    if (hasProcessed.current) {
      return;
    }

    const acceptInvite = async () => {
      // Check for pending invite token from localStorage (after login redirect)
      const pendingToken = localStorage.getItem('pendingInviteToken');
      const inviteToken = token || pendingToken;

      if (!inviteToken) {
        setStatus('error');
        setMessage('Token de convite inválido ou ausente');
        return;
      }

      // Clear pending token if it was used
      if (pendingToken) {
        localStorage.removeItem('pendingInviteToken');
      }

      hasProcessed.current = true; // Mark as processed before API call

      try {
        const authToken = localStorage.getItem('token');

        // Call API to accept invite
        const response = await fetch(`http://localhost:8000/api/v1/invites/${inviteToken}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Include auth token if user is already logged in
            ...(authToken && {
              'Authorization': `Bearer ${authToken}`
            })
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message || 'Convite aceito com sucesso!');

          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            // If user is not logged in, redirect to login
            if (!localStorage.getItem('token')) {
              navigate('/', { state: { message: 'Por favor, faça login para acessar o workspace' } });
            } else {
              // If logged in, redirect to dashboard
              navigate('/chat');
            }
          }, 2000);
        } else {
          // Handle specific error cases
          if (response.status === 401) {
            // Not authenticated - save token and redirect to login
            localStorage.setItem('pendingInviteToken', inviteToken);
            setStatus('error');
            setMessage('Você precisa fazer login para aceitar o convite');
            setTimeout(() => {
              navigate('/', { state: { message: 'Faça login para aceitar o convite' } });
            }, 1500);
          } else if (response.status === 404) {
            setStatus('error');
            setMessage('Convite não encontrado ou já foi utilizado');
          } else if (response.status === 400) {
            setStatus('error');
            setMessage(data.detail || 'Este convite já expirou ou é inválido');
          } else {
            setStatus('error');
            setMessage(data.detail || data.message || 'Erro ao aceitar convite');
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage('Erro ao processar convite. Tente novamente mais tarde.');
      }
    };

    acceptInvite();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#2a2a2a] border border-[#333333] rounded-xl p-8 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/logo-tais.svg" alt="TAIS" className="w-16 h-16" />
        </div>

        {/* Status Icon */}
        <div className="flex justify-center mb-6">
          {status === 'loading' && (
            <Loader2 className="w-16 h-16 text-[#F4CC04] animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircle className="w-16 h-16 text-green-500" />
          )}
          {status === 'error' && (
            <XCircle className="w-16 h-16 text-red-500" />
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-4">
          {status === 'loading' && 'Processando convite...'}
          {status === 'success' && 'Convite aceito!'}
          {status === 'error' && 'Erro ao aceitar convite'}
        </h1>

        {/* Message */}
        <p className="text-[#888888] mb-6">
          {message || 'Aguarde enquanto processamos seu convite...'}
        </p>

        {/* Action Button */}
        {status === 'error' && (
          <button
            onClick={() => navigate('/')}
            className="w-full bg-[#F4CC04] text-black font-semibold py-3 rounded-lg hover:bg-[#d4b003] transition-colors"
          >
            Voltar para o Login
          </button>
        )}

        {status === 'success' && (
          <div className="flex items-center justify-center gap-2 text-[#888888] text-sm">
            <LoadingSpinner size="small" />
            <span>Redirecionando...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
